import { observer } from "mobx-react";
import { NumberQuestion } from "./commonComponents";
import { Box, VBox, Grid, GridItem } from "../../components-v2/Box";
import styles from "./dbot.module.css";
import Big from "big.js";
import { CURRENCY_NOK } from "../../utils/currency";
import { useLocaleFormat, useTranslation } from "../../stores/RootStoreContext";
import { NumberFieldModel } from "../../common/commonForms";

import { InfoButton } from "../../components-v2/InfoButton";

export const IncreaseNominalValue = observer(function ({
  increaseInNominalValue,
  currentNominalValue,
  currentNumberOfShares,
  isProjectLocked,
}: {
  increaseInNominalValue: NumberFieldModel;
  currentNominalValue: number;
  currentNumberOfShares: number;
  isProjectLocked: boolean;
}) {
  const { t } = useTranslation();
  return (
    <>
      <NumberQuestion
        question={t(
          "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.nominalIncrease",
        )}
        prefix={CURRENCY_NOK}
        field={increaseInNominalValue}
        isLocked={isProjectLocked}
      />
      <VBox gap={"md"}>
        <div>
          {t("decisions.increaseShareCapitalAgainstCash.increaseNominalValue.nominalTo")}
          <InfoButton
            infoText={t(
              "decisions.increaseShareCapitalAgainstCash.increaseNominalValue.capitalIncrease-infoText",
            )}
          />
        </div>
        <DescriptionTable
          increaseInNominalValue={increaseInNominalValue}
          currentNominalValue={currentNominalValue}
          currentNumberOfShares={currentNumberOfShares}
        />
      </VBox>
    </>
  );
});

const DescriptionTable = observer(function ({
  increaseInNominalValue,
  currentNominalValue,
  currentNumberOfShares,
}: {
  increaseInNominalValue: NumberFieldModel;
  currentNominalValue: number;
  currentNumberOfShares: number;
}) {
  const { t } = useTranslation();
  const { formatNumber } = useLocaleFormat();

  return (
    <Grid cols={4}>
      <GridItem colSpan={4} className={styles.tableHeader}>
        <Grid cols={4}>
          <GridItem colSpan={2} />
          <GridItem colSpan={1}>{t("before")}</GridItem>
          <GridItem colSpan={1}>{t("after")}</GridItem>
        </Grid>
      </GridItem>

      <GridItem colSpan={2}>
        <Box justifyContent={"end"}>{t("transactions.issueTransaction.nominal-amount")}</Box>
      </GridItem>
      <GridItem colSpan={1}>{formatNumber(currentNominalValue)}</GridItem>
      <GridItem colSpan={1}>
        {formatNumber(
          Big(currentNominalValue)
            .plus(increaseInNominalValue.toNumber() || 0)
            .toNumber(),
        )}
      </GridItem>
      <GridItem colSpan={2}>
        <Box justifyContent={"end"}>
          {t("decisions.increaseShareCapitalAgainstCash.numberOfShares")}
        </Box>
      </GridItem>
      <GridItem colSpan={1}>{formatNumber(currentNumberOfShares)}</GridItem>
      <GridItem colSpan={1}>{formatNumber(currentNumberOfShares)}</GridItem>
      <GridItem colSpan={2}>
        <Box justifyContent={"end"}>{t("shareClasses.capital")}</Box>
      </GridItem>
      <GridItem colSpan={1}>
        {formatNumber(Big(currentNominalValue).times(currentNumberOfShares).toNumber())}
      </GridItem>
      <GridItem colSpan={1}>
        {formatNumber(
          Big(currentNominalValue)
            .plus(increaseInNominalValue.toNumber() || 0)
            .times(currentNumberOfShares)
            .toNumber() || 0,
        )}
      </GridItem>
      <GridItem colSpan={2}>
        <Box justifyContent={"end"}>
          {t("decisions.increaseShareCapitalAgainstCash.increaseNominalValue.capitalIncrease")}
        </Box>
      </GridItem>
      <GridItem colSpan={1} />
      <GridItem colSpan={1}>
        {formatNumber(
          Big(increaseInNominalValue.toNumber() || 0)
            .times(currentNumberOfShares)
            .toNumber() || 0,
        )}
      </GridItem>
    </Grid>
  );
});
